// =============================================
// ArticlePreviewDialog — podgląd, edycja i HTML view artykułu
// =============================================

import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Code, ListTree, Save, Copy, Check, FileText, Link2, ArrowRight, ArrowLeft, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ArticlePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  nodeTitle: string;
  content: {
    final_html?: string;
    draft_html?: string;
    optimized_html?: string;
    enriched_html?: string;
    outline?: any;
    engine_stage?: string;
    quality_score?: number;
    qa_status?: string;
  } | null;
}

export function ArticlePreviewDialog({
  open, onOpenChange, nodeId, nodeTitle, content,
}: ArticlePreviewDialogProps) {
  const { toast } = useToast();
  const html = content?.final_html || content?.draft_html || "";
  const [editedHtml, setEditedHtml] = useState(html);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // Sync when content changes
  useEffect(() => {
    const newHtml = content?.final_html || content?.draft_html || "";
    setEditedHtml(newHtml);
    setHasChanges(false);
  }, [content?.final_html, content?.draft_html]);

  const wordCount = html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  const charCount = html.replace(/<[^>]+>/g, "").length;

  // Extract H2/H3 from HTML for structure tab
  const headings: Array<{ level: number; text: string }> = [];
  const headingRegex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    headings.push({ level: parseInt(match[1]), text: match[2].replace(/<[^>]+>/g, "") });
  }

  // Extract outgoing internal links — matchuje OBOJE:
  //   - relative `<a href="/slug">` (legacy / pre-Link-Integrity strip)
  //   - absolute `<a href="https://domena/slug/">` (po Link Integrity strip pre-publish)
  // Filter "internal" = single-segment path (typowy WP postname permalink).
  // Multi-segment paths (np. /category/slug, /wiki/foo) traktowane jako external/skip.
  const outgoingLinks = (() => {
    const pairs: Array<[string, { slug: string; anchor: string }]> = [];
    const matches = Array.from(
      html.matchAll(/<a[^>]*href="([^"]+)"[^>]*>([^<]*)<\/a>/g),
    );
    for (const m of matches) {
      const raw = m[1];
      const anchor = m[2];
      // Zewnetrzne zrodla (sekcja Zrodla, cytaty enrichmentu) maja rel="...nofollow",
      // internal NIGDY — bez tego filtra zrodlo z jednosegmentowa sciezka
      // (np. mocem.pl/jak-napisac-zaproszenia...) wpadalo na liste "Linkuje do".
      if (/rel="[^"]*nofollow[^"]*"/.test(m[0])) continue;
      let path = "";
      if (raw.startsWith("/")) {
        path = raw;
      } else if (/^https?:\/\//.test(raw)) {
        try {
          path = new URL(raw).pathname;
        } catch {
          continue;
        }
      } else {
        continue;
      }
      // Internal heurystyka: single-segment path (matches /slug lub /slug/)
      if (!/^\/[^/]+\/?$/.test(path)) continue;
      pairs.push([path, { slug: path, anchor }]);
    }
    return [...new Map(pairs).values()];
  })();

  // Fetch incoming links from DB
  const [incomingLinks, setIncomingLinks] = useState<Array<{ title: string; slug: string; relation_type: string }>>([]);
  useEffect(() => {
    if (!open || !nodeId) return;
    (async () => {
      const { data: rels } = await (supabase as any)
        .from("thematic_node_relations")
        .select("from_node_id, relation_type")
        .eq("to_node_id", nodeId);
      if (!rels || rels.length === 0) { setIncomingLinks([]); return; }
      const fromIds = rels.map((r: any) => r.from_node_id);
      const { data: fromNodes } = await (supabase as any)
        .from("thematic_nodes")
        .select("id, title, slug")
        .in("id", fromIds);
      const nodeMap = new Map((fromNodes || []).map((n: any) => [n.id, n]));
      setIncomingLinks(rels.map((r: any) => {
        const n = nodeMap.get(r.from_node_id);
        return { title: n?.title || "?", slug: n?.slug || "?", relation_type: r.relation_type };
      }));
    })();
  }, [open, nodeId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await (supabase as any)
        .from("thematic_node_content")
        .update({ final_html: editedHtml, updated_at: new Date().toISOString() })
        .eq("node_id", nodeId);

      if (error) throw error;
      setHasChanges(false);
      toast({ title: "Zapisano", description: "Artykul zaktualizowany" });
    } catch (err) {
      toast({ title: "Blad", description: err instanceof Error ? err.message : "Blad zapisu", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(editedHtml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyText = async () => {
    const text = html.replace(/<[^>]+>/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold truncate pr-4">
              {nodeTitle}
            </DialogTitle>
            <div className="flex items-center gap-2 shrink-0">
              {content?.quality_score != null && (
                <Badge variant="outline" className="text-xs">
                  QA: {content.quality_score}/100
                </Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {wordCount} slow / {charCount} znakow
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <Tabs defaultValue="preview" className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="preview" className="gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Podglad
              </TabsTrigger>
              <TabsTrigger value="html" className="gap-1.5">
                <Code className="h-3.5 w-3.5" />
                HTML
              </TabsTrigger>
              <TabsTrigger value="structure" className="gap-1.5">
                <ListTree className="h-3.5 w-3.5" />
                Struktura
              </TabsTrigger>
              <TabsTrigger value="links" className="gap-1.5">
                <Link2 className="h-3.5 w-3.5" />
                Linki ({outgoingLinks.length + incomingLinks.length})
              </TabsTrigger>
            </TabsList>

            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={handleCopyText} className="h-7 text-xs">
                {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                {copied ? "Skopiowano" : "Kopiuj tekst"}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCopy} className="h-7 text-xs">
                <Code className="h-3 w-3 mr-1" />
                Kopiuj HTML
              </Button>
              {hasChanges && (
                <Button size="sm" variant="default" onClick={handleSave} disabled={saving} className="h-7 text-xs">
                  <Save className="h-3 w-3 mr-1" />
                  {saving ? "Zapisuję..." : "Zapisz"}
                </Button>
              )}
            </div>
          </div>

          {/* Preview tab — rendered HTML */}
          <TabsContent value="preview" className="flex-1 overflow-y-auto mt-3">
            <div
              className="prose prose-sm dark:prose-invert max-w-none px-6 py-4 border rounded-lg bg-background
                prose-headings:font-bold prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                prose-h2:text-xl prose-h2:mt-6 prose-h2:mb-3 prose-h2:text-violet-700 dark:prose-h2:text-violet-400
                prose-h3:text-base prose-h3:mt-4 prose-h3:mb-2 prose-h3:text-blue-700 dark:prose-h3:text-blue-400
                prose-p:my-2 prose-li:my-0.5 prose-strong:text-foreground
                prose-blockquote:border-l-violet-500 prose-blockquote:bg-muted/30 prose-blockquote:py-1 prose-blockquote:px-4"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </TabsContent>

          {/* HTML tab — editable source */}
          <TabsContent value="html" className="flex-1 overflow-hidden mt-3">
            <textarea
              value={editedHtml}
              onChange={(e) => {
                setEditedHtml(e.target.value);
                setHasChanges(e.target.value !== (content?.final_html || content?.draft_html || ""));
              }}
              className="w-full h-full min-h-[400px] p-4 font-mono text-xs bg-muted/30 border rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-primary"
              spellCheck={false}
            />
          </TabsContent>

          {/* Structure tab — H2/H3 tree */}
          <TabsContent value="structure" className="flex-1 overflow-y-auto mt-3">
            <div className="px-4 py-3 border rounded-lg space-y-1">
              {headings.length === 0 ? (
                <p className="text-sm text-muted-foreground">Brak naglowkow w artykule</p>
              ) : (
                headings.map((h, i) => (
                  <div key={i} className={`flex items-center gap-2 ${h.level === 3 ? "ml-6" : ""}`}>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      h.level === 2
                        ? "bg-violet-500/20 text-violet-700 dark:text-violet-300"
                        : "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                    }`}>
                      H{h.level}
                    </span>
                    <span className={`text-sm ${h.level === 2 ? "font-medium" : "text-muted-foreground"}`}>
                      {h.text}
                    </span>
                  </div>
                ))
              )}
              <div className="pt-3 mt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                <span>{headings.filter((h) => h.level === 2).length} H2</span>
                <span>{headings.filter((h) => h.level === 3).length} H3</span>
                <span>{wordCount} slow</span>
                <span>{charCount} znakow</span>
              </div>
            </div>
          </TabsContent>

          {/* Links tab — internal linking */}
          <TabsContent value="links" className="flex-1 overflow-y-auto mt-3">
            <div className="px-4 py-3 border rounded-lg space-y-4">
              {/* Outgoing — ten artykuł linkuje do */}
              <div>
                <p className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1.5">
                  <ArrowRight className="h-3.5 w-3.5" />
                  Linkuje do ({outgoingLinks.length})
                </p>
                {outgoingLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">Brak linków wewnętrznych w tekście</p>
                ) : (
                  <div className="space-y-1 ml-5">
                    {outgoingLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="text-xs text-muted-foreground font-mono">{link.slug}</span>
                        <span className="text-muted-foreground">—</span>
                        <span>{link.anchor}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Incoming — te artykuły linkują tutaj */}
              <div>
                <p className="text-xs font-medium text-green-400 mb-2 flex items-center gap-1.5">
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Linkują tutaj ({incomingLinks.length})
                </p>
                {incomingLinks.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">Żaden artykuł nie linkuje do tego node'a</p>
                ) : (
                  <div className="space-y-1 ml-5">
                    {incomingLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <span className="font-medium">{link.title}</span>
                        <span className="text-xs text-muted-foreground">({link.relation_type})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Warnings */}
              {outgoingLinks.length < 2 && (
                <div className="flex items-center gap-2 text-xs text-yellow-500 mt-2">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Mniej niż 2 linki wychodzące — SRO wymaga min 2 per node
                </div>
              )}
              {incomingLinks.length === 0 && (
                <div className="flex items-center gap-2 text-xs text-yellow-500">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Izolowany node — żaden artykuł nie linkuje tutaj
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
